/* eslint-disable global-require */

/* eslint-disable @typescript-eslint/no-var-requires */
import blobToHash from 'blob-to-hash';
import { useEffect, useRef } from 'react';
import { ReactForm } from '@ninja/utilities';
import {
	GetDocumentRetrieveAllResponse,
	PostDocumentUploadError,
	postDocumentUpload,
} from '@shory-health-individual/customer-api';
import { DocumentTypes, UploadOperationTypes, toKebabCase } from '../../../../common';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { useUploadedDocumentConfigs } from '../../../app-init-data';
import { DocumentsForm } from './types';

type Props = {
	encryptedJourneyId: string;
	memberDocuments: GetDocumentRetrieveAllResponse;
	form: ReactForm<DocumentsForm>;
};

const FieldKeys: Record<DocumentTypes, string> = {
	[DocumentTypes.PassportScan]: 'passportFile',
	[DocumentTypes.VisaScan]: 'visaFile',
	[DocumentTypes.LabourContract]: 'labourContractFile',
	[DocumentTypes.InsuranceContinuityCertificateOrProofOfPayment]: 'insuranceContinuityFile',
};

const getField = (memberId: string, documentType: DocumentTypes) => `${memberId}.${FieldKeys[documentType]}`;

const useUploadFiles = (props: Props) => {
	const { memberDocuments, encryptedJourneyId, form } = props;
	const { translate: t, apiConnectionOptions } = useAppPlatformContextOptions();
	const { getUploadedDocumentConfigByType } = useUploadedDocumentConfigs();

	const filesHashes = useRef<Record<string, string>>({});

	const getDocumentConfigs = (documentType: DocumentTypes) => {
		const documentConfig = getUploadedDocumentConfigByType(documentType);
		const allowedTypes = (documentConfig?.allowedTypes || '').split(',');

		const typesMap = {
			PNG: { 'image/png': ['.png'] },
			JPG: { 'image/jpg': ['.jpg'] },
			JPEG: { 'image/jpeg': ['.jpeg'] },
			HEIC: { 'image/heic': ['.heic'] },
			HEIF: { 'image/heif': ['.heif'] },
			PDF: { 'text/pdf': ['.pdf'] },
		};

		let accept: Record<string, string[]> = {};

		for (const key of allowedTypes) {
			if (key in typesMap) {
				const inner = typesMap[key as keyof typeof typesMap];

				accept = { ...accept, ...inner };
			}
		}

		let typesArr: string[] = [];

		for (const key of Object.keys(accept)) {
			typesArr = [...typesArr, ...accept[key]];
		}

		return {
			accept,
			fileSize: (documentConfig?.maxAllowedSizeInKb ?? 1024) / 1024,
			typesText: typesArr.join(', '),
		};
	};

	const onUploadFile = async (memberId: string, file: File, documentType: DocumentTypes) => {
		let finalFile = file;

		if (file.type.includes('heic') || file.type.includes('heif')) {
			const heic2any = require('heic2any');
			const blob = await (await fetch(URL.createObjectURL(file))).blob();
			const fileBlob: Blob = await heic2any({
				blob,
				toType: 'image/jpeg',
				quality: 50,
			});

			finalFile = new File([fileBlob], finalFile?.name?.split('.')[0] + '.jpeg');
		}

		try {
			const response = await postDocumentUpload(apiConnectionOptions)({
				uploadRef: memberDocuments[memberId]?.uploadRef,
				encryptedJourneyId,
				memberId,
				type: documentType as unknown as string,
				file: finalFile as unknown as string,
				operationType: UploadOperationTypes.Insertion,
			});

			const hash = await blobToHash('sha256', file);

			filesHashes.current = { ...filesHashes.current, [getField(memberId, documentType)]: hash };

			form.setFieldValue(getField(memberId, documentType), response?.fileUrl as never);
		} catch (e) {
			const errors = e as PostDocumentUploadError;
			const code = errors.mapped?.[0]?.code;

			if (code) {
				if (code === 'InvalidDocumentFileSize') {
					const fileSize = getDocumentConfigs(documentType).fileSize;

					form.setFieldError(
						getField(memberId, documentType),
						t('shared.error-messages.invalid-document-file-size', { fileSize })
					);

					return;
				}

				if (code === 'InvalidDocumentExtension') {
					const typesText = getDocumentConfigs(documentType).typesText;

					form.setFieldError(
						getField(memberId, documentType),
						t('shared.error-messages.invalid-document-extension', { typesText })
					);

					return;
				}

				const translation = t(`shared.error-messages.${toKebabCase(code)}`) ?? t('shared.error-messages.general-error');

				form.setFieldError(getField(memberId, documentType), translation);

				return;
			}
			form.setFieldError(getField(memberId, documentType), t('shared.error-messages.general-error'));
		}
	};

	const validateFile = async (
		memberId: string,
		documentType: DocumentTypes,
		acceptedFile?: File,
		rejectedFileCode?: string
	) => {
		let error: string | undefined = undefined;

		const documentConfigs = getDocumentConfigs(documentType);

		if (rejectedFileCode) {
			if (rejectedFileCode === 'file-too-large') {
				error = t('shared.error-messages.invalid-document-file-size', { fileSize: documentConfigs.fileSize });
			} else {
				error = t('shared.error-messages.invalid-document-extension', { typesText: documentConfigs.typesText });
			}
		} else if (acceptedFile && acceptedFile.size > 0) {
			const currentFileHash = await blobToHash('sha256', acceptedFile);

			const isDuplicate = Object.values(filesHashes.current).filter((hash) => hash === currentFileHash).length > 0;

			if (isDuplicate) error = t('shared.error-messages.invalid-repeated-document');
		}

		if (error) {
			form.setFieldError(getField(memberId, documentType), error);

			return error;
		} else {
			form.setFieldError(getField(memberId, documentType), undefined);
		}
	};

	const getFileValue = (memberId: string, documentType: DocumentTypes) =>
		form.getValues(getField(memberId, documentType)) as string | undefined;

	const getFileError = (memberId: string, documentType: DocumentTypes) => {
		const state = form.getFieldState(getField(memberId, documentType));

		if (state.isTouched === false) return undefined;

		return state.error?.message;
	};

	const setDuplicateFilesErrors = () => {
		const temp: Record<string, string[]> = {};

		for (const [field, hash] of Object.entries(filesHashes.current)) {
			if (temp[hash]) temp[hash].push(field);
			else temp[hash] = [field];
		}

		for (const fields of Object.values(temp)) {
			if (fields.length > 1) {
				fields.forEach((field) => form.setFieldError(field, t('shared.error-messages.invalid-repeated-document')));
			}
		}
	};

	const onClearFile = (memberId: string, documentType: DocumentTypes) => {
		const currentSavedFiles = { ...filesHashes.current };

		delete currentSavedFiles[getField(memberId, documentType)];

		filesHashes.current = currentSavedFiles;

		setDuplicateFilesErrors();

		form.setFieldValue(getField(memberId, documentType), undefined as never);
	};

	// On did mount fill hash array with the current files and validate them
	useEffect(() => {
		const hashes: Record<string, string> = {};

		for (const [memberId, member] of Object.entries(memberDocuments)) {
			for (const document of member.documents) {
				const field = getField(memberId, document.documentType as DocumentTypes);

				// TODO: Download files and get hash
				hashes[field] = document.filePath;
			}
		}

		setDuplicateFilesErrors();
	}, [memberDocuments]);

	return {
		onUploadFile,
		validateFile,
		onClearFile,
		getFileValue,
		getFileError,
		getDocumentConfigs,
	};
};

export default useUploadFiles;
